import { cpvLevel, cpvParent } from '@tenders/shared';

/**
 * Tender detail path helpers. The slug is produced by the API (locale-neutral,
 * derived from the source title); the frontend only assembles/parses the URL.
 */

/**
 * Build the tender detail path. With a slug the URL is `/tender/{slug}-{id}`
 * (keyword-bearing); without one it's the bare `/tender/{id}`. The id (a cuid) is
 * always the trailing segment, so {@link extractTenderId} can recover it.
 */
export function tenderPath(slug: string | null | undefined, id: string): string {
  return slug ? `/tender/${slug}-${id}` : `/tender/${id}`;
}

/**
 * Recover the tender id from a route param that may be slugged (`{slug}-{id}`) or
 * bare (`{id}`). The id is the substring after the LAST hyphen; a cuid never
 * contains `-`, so this is unambiguous. Returns the whole param when there's no
 * hyphen (bare id or malformed input).
 */
export function extractTenderId(param: string): string {
  const dash = param.lastIndexOf('-');
  return dash === -1 ? param : param.slice(dash + 1);
}

/**
 * Price-reference path (#46), same shape as {@link tenderPath}: with a slug it's
 * `/prices/{slug}-{ikpu}` (keyword-bearing, from the API's neutral product slug),
 * else the bare `/prices/{ikpu}`. The ИКПУ code (digits + dots, never a hyphen) is
 * always the trailing segment, so {@link extractIkpu} recovers it unambiguously.
 */
export function pricePath(slug: string | null | undefined, ikpu: string): string {
  return slug ? `/prices/${slug}-${ikpu}` : `/prices/${ikpu}`;
}

/**
 * Recover the ИКПУ code from a `/prices/:ikpu` route param that may be slugged
 * (`{slug}-{ikpu}`) or bare (`{ikpu}`). The code is the substring after the LAST
 * hyphen; ИКПУ codes are digits + dots and never contain `-`, so this is
 * unambiguous. Returns the whole param when there's no hyphen (bare code).
 */
export function extractIkpu(param: string): string {
  const dash = param.lastIndexOf('-');
  return dash === -1 ? param : param.slice(dash + 1);
}

/**
 * The next ИКПУ level up: `26.20.11.110` → `26.20.11`; a bare section → null.
 *
 * Exists because the base-rates panel links to `/prices/:ikpu` with a tender's
 * OWN granular code while that page deliberately does not roll up to ancestors
 * (`price-reference.repository.ts` `ikpuSelfAndDescendants` vs base-rates'
 * `ikpuOutcomeFilter`, which rolls up). Measured 2026-09-16: 1,929 of the 4,780
 * live tenders that render the panel link to a code with fewer than 8 credible
 * awards, so the page had nothing to show. One level up is what turns that
 * into a page.
 *
 * A CPV code (Spain, eight digits) has no dot; its level is in the trailing
 * zeros, so its parent is `cpvParent` (`45233140` → `45233100`, a division →
 * null). The API matches a CPV `/prices/:ikpu` as its subtree, so the parent's
 * page holds the child's awards, as a dotted parent's does.
 */
export function parentIkpu(code: string): string | null {
  if (cpvLevel(code) !== null) return cpvParent(code);
  const dot = code.lastIndexOf('.');
  return dot === -1 ? null : code.slice(0, dot);
}
